import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRequest } from 'utils/requests';
import { Loading, EmptyState } from 'components/common/Elements/Elements';
import { Box } from '@p81-common/ui';

import SubscriptionHeader from './SubscriptionHeader';
import AddonsList from './AddonsList';
import SaseSkusList from './SaseSkusList';

interface Addon {
  id: string;
  friendlyName: string;
  included: boolean;
  quantity: number | null;
  unit: string | null;
}

interface Sku {
  sku: string;
  expiresAt: string;
  quantity: number | null;
}

interface LicensingData {
  customerId: string;
  subscriptionExpirationDate: string | null;
  addons: Addon[] | null;
  skus: Sku[] | null;
  chargebeeError: boolean;
  skusError: boolean;
}

interface LicensingProps {
  customerId: string;
  // The tenant's pricing plan, already available on the single-tenant page
  // (the "Pricing Plan" field). Used to decide SKU-section visibility.
  plan: string | null;
}

const Licensing = ({ customerId, plan }: LicensingProps) => {
  const { t } = useTranslation('activity');
  const [data, setData] = useState<LicensingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getRequest(`/customer/licensing/${customerId}`)
      .then((res: { body: LicensingData }) => {
        if (!cancelled) {
          if (res.body.chargebeeError) {
            // eslint-disable-next-line no-console
            console.error(
              `Licensing: Chargebee side failed (subscription/add-ons) for tenant ${customerId}`
            );
          }
          if (res.body.skusError) {
            // eslint-disable-next-line no-console
            console.error(
              `Licensing: SKU side failed (MAPI/CPM) for tenant ${customerId}`
            );
          }
          setData(res.body);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setLoading(false);
          setError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [customerId]);

  if (loading) {
    return <Loading data-testid="licensing-loading" />;
  }

  if (error || !data) {
    return (
      <EmptyState message={t('activity:customers:LICENSING_EMPTY_STATE')} />
    );
  }

  return (
    <Box sx={{ padding: '16px 0' }}>
      <SubscriptionHeader
        subscriptionExpirationDate={data.subscriptionExpirationDate}
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 6,
          flexWrap: 'wrap'
        }}
      >
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <AddonsList addons={data.addons || []} error={data.chargebeeError} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 280 }}>
          <SaseSkusList skus={data.skus} planId={plan} error={data.skusError} />
        </Box>
      </Box>
    </Box>
  );
};

export default Licensing;
